/**
 * Surface content — 3.3 Discover · the requirements register.
 *
 * ZERO literals in the component (build brief §0a): every figure, name, id,
 * route and sentence the register renders is declared here or read from
 * `@/demo/data`. Counts a click can move are NOT here — they come from the
 * store (`selectRequirements`), so approving an open decision moves the chip
 * row, the gate sentence and the row itself together.
 *
 * The register's own claim is traceability: a requirement is only as good as
 * the moment it was said. Every row therefore carries a SOURCE that links to
 * the capture log, and the chain runs on from there — requirement → module →
 * regression test → AMS runbook line — with each stage linking to its surface.
 */

import { CLIENT_CONTACT, PHASES, VAN_DIJK, agentById, heroProject, thinSpace } from '@/demo/data'

const PROJECT = heroProject()
const REQUIREMENTS = VAN_DIJK.requirements
const FIT_GAP = VAN_DIJK.fitGap
const PAX = agentById('pax')
const DISCOVER = PHASES.find((p) => p.key === 'discover')!

const projectHref = (segment: string): string => `/project/${PROJECT.id}/${segment}`

/** Every affordance on this surface lands on one of these — no dead links. */
export const HREFS = {
  interviews: projectHref('interviews'),
  requirements: projectHref('requirements'),
  blueprint: projectHref('blueprint'),
  build: projectHref('build'),
  test: projectHref('test'),
  ams: projectHref('ams'),
  retailTemplate: '/library/templates/retail-rollout',
} as const

/* ── Chrome ─────────────────────────────────────────────────────────────── */

export const REGISTER_PAGE = {
  moduleLabel: 'PX · Project',
  contextPill: `${thinSpace(REQUIREMENTS.total)} REQUIREMENTS`,
  title: 'Requirements register',
  standfirst:
    'Everything the intake listener captured, grouped into epics, judged against standard Odoo, and carrying a ' +
    'link back to the moment in the call where it was said.',
  captureLine: `${PAX.name} · ${PAX.runs} · ${PAX.reviewGate} · ${PAX.acceptancePct}% of its rows accepted as written.`,
  contextLinks: {
    lead: 'The register is the Discover gate’s evidence — the blueprint is written from it.',
    interviewsLabel: 'Interviews & capture log',
    blueprintLabel: 'Solution blueprint',
  },
} as const

/** Section anchors — the register links to itself as a traceability stage. */
export const ANCHORS = {
  register: 'requirements-register',
  gate: 'discover-gate',
} as const

/* ── Fit-gap verdicts ───────────────────────────────────────────────────── */

/** The verdict against standard Odoo. `Template v4` is the library module PX
 *  proposes rather than builds — hence the mint (proposed) reading. */
export type FitGapVerdict = 'Standard' | 'Template v4' | 'New build'

/* ── Filter chips (the real register counts; the rows are a seeded page) ─── */

export type ChipKey = 'all' | 'standard' | 'library' | 'new-build'

export interface FilterChip {
  key: ChipKey
  label: string
  count: number
  /** null = the "All" chip, which filters nothing. */
  verdict: FitGapVerdict | null
}

export const FILTER_CHIPS: readonly FilterChip[] = [
  { key: 'all', label: 'All', count: REQUIREMENTS.total, verdict: null },
  { key: 'standard', label: 'Standard', count: FIT_GAP.standard, verdict: 'Standard' },
  { key: 'library', label: 'Library module', count: FIT_GAP.library, verdict: 'Template v4' },
  { key: 'new-build', label: 'New build', count: FIT_GAP.newBuild, verdict: 'New build' },
] as const

export const CHIP_ALL: ChipKey = 'all'

export const FILTERS = {
  chipsLabel: 'Fit-gap',
  chipsLegend: 'Filter the register by fit-gap verdict',
  epicLabel: 'Epic',
  countsNote: 'Chip counts are the whole register; the table below is a seeded page of it.',
} as const

/* ── Epics ──────────────────────────────────────────────────────────────── */

export type EpicKey = 'all' | 'pricing' | 'promotions' | 'store-hierarchy' | 'loyalty' | 'inventory'

export interface Epic {
  key: EpicKey
  label: string
}

export const EPICS: readonly Epic[] = [
  { key: 'all', label: 'All epics' },
  { key: 'pricing', label: 'Pricing' },
  { key: 'promotions', label: 'Promotions' },
  { key: 'store-hierarchy', label: 'Store hierarchy' },
  { key: 'loyalty', label: 'Loyalty' },
  { key: 'inventory', label: 'Inventory' },
] as const

export const EPIC_ALL: EpicKey = 'all'

export const epicLabel = (key: EpicKey): string => EPICS.find((e) => e.key === key)?.label ?? key

/** The epic the open decisions hold up — named in the gate sentence. */
export const BLOCKED_EPIC: EpicKey = 'pricing'

/* ── Sources (channel · moment — every one links to where it was captured) ── */

export type SourceKey = 'pricing-workshop' | 'mail-14-aug' | 'whatsapp-15-aug' | 'architecture-call'

export interface Source {
  key: SourceKey
  channel: string
  /** The moment inside that channel — a clock time or the day it arrived. */
  moment: string
  href: string
  /** Read out to assistive tech in place of the bare "channel · moment". */
  linkLabel: string
}

const source = (s: Source): Source => s

export const SOURCES: Record<SourceKey, Source> = {
  'pricing-workshop': source({
    key: 'pricing-workshop',
    channel: 'Pricing workshop',
    moment: '10:42',
    href: HREFS.interviews,
    linkLabel: 'open the pricing workshop transcript at 10:42',
  }),
  'mail-14-aug': source({
    key: 'mail-14-aug',
    channel: 'Mail',
    moment: '14 Aug',
    href: HREFS.interviews,
    linkLabel: 'open the mail thread of 14 August in the capture log',
  }),
  'whatsapp-15-aug': source({
    key: 'whatsapp-15-aug',
    channel: 'WhatsApp',
    moment: '15 Aug',
    href: HREFS.interviews,
    linkLabel: 'open the WhatsApp capture of 15 August',
  }),
  'architecture-call': source({
    key: 'architecture-call',
    channel: 'Architecture call',
    moment: '09:20',
    href: HREFS.interviews,
    linkLabel: 'open the architecture call transcript at 09:20',
  }),
}

export const sourceOf = (key: SourceKey): Source => SOURCES[key]

/* ── The register rows ──────────────────────────────────────────────────── */

export type RequirementStatus = 'Approved' | 'Open decision'

export interface ModuleNote {
  module: string
  href: string
}

export interface RequirementRow {
  id: number
  title: string
  epic: EpicKey
  verdict: FitGapVerdict
  /** Set when the verdict names a library asset the client can open. */
  verdictHref: string | null
  source: SourceKey
  effortH: number
  status: RequirementStatus
  /** The module implementing this row, on the build board. */
  module: ModuleNote | null
  /** What the client still owes — open rows only. */
  openNote: string | null
}

const row = (r: RequirementRow): RequirementRow => r

/** The requirement the gate's approve control names. */
export const GATE_REQUIREMENT_ID = 61

const SEEDED_ROWS: readonly RequirementRow[] = [
  row({
    id: 12,
    title: 'Store hierarchy: four regions, 38 stores, one price book per region',
    epic: 'store-hierarchy',
    verdict: 'Standard',
    verdictHref: null,
    source: 'architecture-call',
    effortH: 6,
    status: 'Approved',
    module: null,
    openNote: null,
  }),
  row({
    id: 23,
    title: 'Stock moves between stores without a purchase order',
    epic: 'inventory',
    verdict: 'Standard',
    verdictHref: null,
    source: 'mail-14-aug',
    effortH: 8,
    status: 'Approved',
    module: null,
    openNote: null,
  }),
  row({
    id: 47,
    title: 'Pricing hierarchy set at store level',
    epic: 'pricing',
    verdict: 'Standard',
    verdictHref: null,
    source: 'pricing-workshop',
    effortH: 12,
    status: 'Approved',
    module: null,
    openNote: null,
  }),
  row({
    id: 48,
    title: 'Promotion override per store group',
    epic: 'promotions',
    verdict: 'Template v4',
    verdictHref: HREFS.retailTemplate,
    source: 'pricing-workshop',
    effortH: 18,
    status: 'Approved',
    module: { module: 'px_promo_override', href: HREFS.build },
    openNote: null,
  }),
  row({
    id: 52,
    title: 'Promotion end date can be pulled forward mid-week',
    epic: 'promotions',
    verdict: 'Standard',
    verdictHref: null,
    source: 'whatsapp-15-aug',
    effortH: 4,
    status: 'Approved',
    module: null,
    openNote: null,
  }),
  row({
    id: GATE_REQUIREMENT_ID,
    title: 'Franchise stores price independently of their region',
    epic: 'pricing',
    verdict: 'New build',
    verdictHref: null,
    source: 'architecture-call',
    effortH: 30,
    status: 'Open decision',
    module: null,
    openNote:
      'Waiting on the client: whether the eleven franchise stores keep their own price book after go-live, or ' +
      'inherit the region’s.',
  }),
  row({
    id: 64,
    title: 'Margin floor per product family overrides any promotion',
    epic: 'pricing',
    verdict: 'Template v4',
    verdictHref: null,
    source: 'mail-14-aug',
    effortH: 18,
    status: 'Open decision',
    module: null,
    openNote:
      'Waiting on the client: the floor percentage per family, and who may override it in store.',
  }),
  row({
    id: 70,
    title: 'Negative stock allowed for pre-orders only',
    epic: 'inventory',
    verdict: 'Standard',
    verdictHref: null,
    source: 'mail-14-aug',
    effortH: 6,
    status: 'Approved',
    module: null,
    openNote: null,
  }),
  row({
    id: 78,
    title: 'Loyalty tier discount stacks with a promotion, capped at 30%',
    epic: 'loyalty',
    verdict: 'New build',
    verdictHref: null,
    source: 'whatsapp-15-aug',
    effortH: 22,
    status: 'Approved',
    module: null,
    openNote: null,
  }),
  row({
    id: 83,
    title: 'Store groups inherit their region’s assortment',
    epic: 'store-hierarchy',
    verdict: 'Standard',
    verdictHref: null,
    source: 'architecture-call',
    effortH: 9,
    status: 'Approved',
    module: null,
    openNote: null,
  }),
]

/**
 * Open decisions first, then by number. The rank is taken from the PRISTINE
 * status, so approving a row does not pull it out from under the pointer —
 * it stays where it was and changes colour.
 */
const ORDERED_ROWS: readonly RequirementRow[] = [...SEEDED_ROWS].sort((a, b) => {
  const weight = (r: RequirementRow) => (r.status === 'Open decision' ? 0 : 1)
  return weight(a) - weight(b) || a.id - b.id
})

/** The rows projected forward from the seed by the approvals recorded so far. */
export const requirementRows = (approvedIds: readonly number[]): readonly RequirementRow[] =>
  ORDERED_ROWS.map((r) =>
    approvedIds.includes(r.id) ? { ...r, status: 'Approved' as const, openNote: null } : r
  )

export const chipByKey = (key: ChipKey): FilterChip =>
  FILTER_CHIPS.find((c) => c.key === key) ?? {
    key: CHIP_ALL,
    label: FILTER_CHIPS[0]?.label ?? 'All',
    count: REQUIREMENTS.total,
    verdict: null,
  }

export const filterRows = (
  rows: readonly RequirementRow[],
  chip: ChipKey,
  epic: EpicKey
): readonly RequirementRow[] => {
  const verdict = chipByKey(chip).verdict
  return rows.filter((r) => (verdict === null || r.verdict === verdict) && (epic === EPIC_ALL || r.epic === epic))
}

/** Open decisions still holding up the blocked epic, in the rows shown. */
export const blockingRows = (rows: readonly RequirementRow[]): readonly RequirementRow[] =>
  rows.filter((r) => r.status === 'Open decision' && r.epic === BLOCKED_EPIC)

export const totalEffort = (rows: readonly RequirementRow[]): number =>
  rows.reduce((sum, r) => sum + r.effortH, 0)

export const REGISTER_TABLE = {
  label: 'Register',
  heading: 'Requirements, open decisions first',
  caption: 'PAX writes the row; the fit-gap verdict is a person’s call; the source is where it was said.',
  columns: {
    id: '#',
    requirement: 'Requirement',
    verdict: 'Fit-gap',
    source: 'Source',
    effort: 'Effort',
    status: 'Status',
  },
  effortUnit: 'h',
  effortHeaderNote: 'hours',
  moduleLead: 'implemented by',
  templateLinkLabel: 'library template',
  emptyLine: 'No seeded row matches that pair of filters — the rest of the register is not in this prototype.',
  filteredNote: (shown: number, seeded: number): string =>
    `Showing ${shown} of the ${seeded} seeded rows`,
} as const

/* ── Fit-gap summary ────────────────────────────────────────────────────── */

export interface FitGapSegment {
  key: ChipKey
  verdict: FitGapVerdict
  label: string
  pct: number
  count: number
}

export const FIT_GAP_SEGMENTS: readonly FitGapSegment[] = [
  { key: 'standard', verdict: 'Standard', label: 'Standard Odoo', pct: FIT_GAP.standardPct, count: FIT_GAP.standard },
  { key: 'library', verdict: 'Template v4', label: 'Library module', pct: FIT_GAP.libraryPct, count: FIT_GAP.library },
  { key: 'new-build', verdict: 'New build', label: 'New build', pct: FIT_GAP.newBuildPct, count: FIT_GAP.newBuild },
] as const

export const FIT_GAP_SUMMARY = {
  label: 'Fit-gap summary',
  heading: 'Where the 142 requirements land',
  barLabel: 'Fit-gap split across the register',
  note:
    `${FIT_GAP.standardPct}% needs no code at all, ${FIT_GAP.libraryPct}% is a module PX has already built for ` +
    `another retailer, and ${FIT_GAP.newBuildPct}% is genuinely new. The estimate the client signs is the last ` +
    'number, not the first.',
  segmentLine: (s: FitGapSegment): string => `${s.pct}% · ${thinSpace(s.count)} requirements`,
} as const

/* ── Traceability chain ─────────────────────────────────────────────────── */

export interface TraceStage {
  key: string
  stage: string
  text: string
  to: string
}

export const TRACEABILITY = {
  label: 'Traceability',
  heading: 'One requirement, end to end',
  lead: 'Every stage below is the same requirement, and every stage links to the surface that owns it:',
  chain: [
    { key: 'moment', stage: 'Moment', text: 'Pricing workshop · 10:42', to: HREFS.interviews },
    { key: 'requirement', stage: 'Requirement', text: '#48 Promotion override per store group', to: HREFS.requirements },
    { key: 'module', stage: 'Module', text: 'px_promo_override', to: HREFS.build },
    { key: 'test', stage: 'Regression test', text: 'TC-118 · override beats the base price', to: HREFS.test },
    { key: 'ams', stage: 'AMS runbook', text: 'Runbook line 7 · promotion overrides', to: HREFS.ams },
  ] as readonly TraceStage[],
  closing:
    'When the upgrade scout flags this module against Odoo 20, the chain runs the other way: the runbook line ' +
    'names the test, the test names the requirement, and the requirement names the person who asked for it.',
} as const

/* ── Discover gate ──────────────────────────────────────────────────────── */

const NUMBER_WORDS: Record<number, string> = { 0: 'no', 1: 'one', 2: 'two', 3: 'three' }

const numberWord = (n: number): string => NUMBER_WORDS[n] ?? thinSpace(n)

export const GATE = {
  label: `Gate · ${DISCOVER.name}`,
  heading: `${DISCOVER.subLabel} — ${DISCOVER.gate}`,
  /** Reads the projected counts, so an approval moves this sentence. */
  sentence: (approved: number, total: number, blockers: number): string =>
    blockers === 0
      ? `${thinSpace(approved)} of ${thinSpace(total)} approved by the client; nothing blocks the ${epicLabel(BLOCKED_EPIC).toLowerCase()} epic any more.`
      : `${thinSpace(approved)} of ${thinSpace(total)} approved by the client; ${numberWord(blockers)} open ` +
        `${blockers === 1 ? 'decision blocks' : 'decisions block'} the ${epicLabel(BLOCKED_EPIC).toLowerCase()} epic.`,
  awaitingPill: (blockers: number): string =>
    `Awaiting client · ${thinSpace(blockers)} ${blockers === 1 ? 'item' : 'items'}`,
  reminderPill: 'Reminder sent',
  clearedPill: 'Pricing epic clear',
  gateNote:
    'The gate does not open on a majority. It opens when the open decisions are answered by a named person on ' +
    'the client side, and the register records who and when.',
} as const

export const GATE_APPROVAL = {
  /** Payload for `actRecorded` — the component supplies no literals. */
  requirement: GATE_REQUIREMENT_ID,
  by: CLIENT_CONTACT.name,
  byRole: `${CLIENT_CONTACT.role} · ${CLIENT_CONTACT.org}`,
  atLabel: '09:05',
  buttonLabel: `Approve #${GATE_REQUIREMENT_ID} as ${CLIENT_CONTACT.name}`,
  consequence:
    'Records the client’s approval on the register · moves the gate count · logged with name and time.',
  decidedChip: (by: string, at: string): string => `Approved by ${by} · ${at}`,
  decidedLine: (by: string, role: string, at: string): string =>
    `#${GATE_REQUIREMENT_ID} approved by ${by} (${role}) at ${at}. The register carries the name, not just the count.`,
  recordLinkLabel: 'view the row →',
} as const

/* ── Density ────────────────────────────────────────────────────────────── */

export const densityNote = (
  seeded: readonly RequirementRow[],
  shown: readonly RequirementRow[],
  total: number
): string =>
  `Showing ${thinSpace(shown.length)} of ${thinSpace(total)} requirements — ${thinSpace(seeded.length)} rows are ` +
  `seeded in this prototype and the filter chips carry the real register counts (${thinSpace(FIT_GAP.standard)} ` +
  `standard · ${thinSpace(FIT_GAP.library)} library module · ${thinSpace(FIT_GAP.newBuild)} new build). Every ` +
  `source cites the moment it was said and opens the capture log at it. The full register export and the ` +
  `client’s own comment thread are not in this prototype; ${thinSpace(totalEffort(shown))} h of estimate is the ` +
  `sum of the rows shown, not of the ${thinSpace(total)}.`
